import type { AccountType } from '../types/account.js';

// Comparator works on any shape that carries accountType + name -- we keep it
// narrow so both the client's wire `Account` (IsoTimestamp strings) and the
// server's `StoredAccount` (Firestore Timestamps) can use it without casts.
export type SortableAccount = { accountType: AccountType; name: string };

// Spec: within each parent's children, sort by accountType with locations
// last; within the same accountType, alphabetical by name. The explicit
// order numbers reflect how operators scan a tree: groups/brands/regions
// first (they're navigational containers), locations last (they're leaves
// and are often the bulk of the list).
//
// Firestore doesn't support custom enum-order sorting; sort is applied in
// memory on both the server and the client. At v1 scale (<1000 accounts
// per project) the cost is negligible.
const ACCOUNT_TYPE_ORDER: Record<AccountType, number> = {
  group: 1,
  brand: 2,
  head_office: 3,
  region: 4,
  dealership: 5,
  location: 6,
};

export function compareAccountsForTree(a: SortableAccount, b: SortableAccount): number {
  const ao = ACCOUNT_TYPE_ORDER[a.accountType] ?? 99;
  const bo = ACCOUNT_TYPE_ORDER[b.accountType] ?? 99;
  if (ao !== bo) return ao - bo;
  return a.name.localeCompare(b.name);
}

export function sortAccountsForTree<T extends SortableAccount>(accounts: T[]): T[] {
  return [...accounts].sort(compareAccountsForTree);
}
